import { type MouseEvent, useContext, useState } from 'react';

import { mdiClipboardAccountOutline } from '@mdi/js';
import { ArrowBackIos } from '@mui/icons-material';
import { Table, TableBody, TableContainer } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import RosterDeleteModal from './RosterDeleteModal';
import RosterEditModal from './RosterEditModal';
import RosterPermissionsError from './RosterPermissionsError';
import RosterSendingCalendarInviteModal from './RosterSendingCalendarInviteModal';
import RosterTableHead from './RosterTableHead';
import RosterTableRow from './RosterTableRow';
import { type Order, type RosterTableColumns } from './types';
import { CLASS_CALENDAR, CLASS_CALENDAR_EMAIL } from '../../config/apiPaths';
import { ClassContext } from '../../context/ClassContext';
import { RosterContext } from '../../context/RosterContext';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getComparator } from '../../utils/sortingUtils';
import { getUrls, isDevelopmentEnv } from '../../utils/url';
import { ClassContainer } from '../../views/ClassModal/ClassContainer';
import Loadable from '../common/loadable';
import MainLayout, { type NavControlItem } from '../layout/MainLayout';

const RosterView = () => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<RosterTableColumns>('first_name');
    const {
        classId,
        classUuid,
        userUuid,
        isDemo,
        openEditModal,
        setOpenEditModal,
        openDeleteModal,
        setOpenDeleteModal,
        setSelectedStudentId,
        selectedStudentId,
        roster,
        loading,
        fetchingStudents,
        showAddStudentsButton,
        isAuthorized,
        sendingCalendarInvite,
        setSendingCalendarInvite,
        adminSettings,
        studentGroups,
        isNarrowStyle,
        isFullScreen,
        onBack
    } = useContext(RosterContext);
    const { classData } = useContext(ClassContext);

    const { t, ready } = useTranslation('roster');
    const history = useHistory();

    if (!isAuthorized && !loading && ready) {
        return (
            <MainLayout icon={mdiClipboardAccountOutline} title={t('roster-title')} narrowStyle={isNarrowStyle}>
                <RosterPermissionsError />
            </MainLayout>
        );
    }

    const handleRowClick = (event: MouseEvent<HTMLButtonElement>, studentId) => {
        setSelectedStudentId(studentId);
    };

    const hasStudentGroups = !!studentGroups?.length;
    const renderTableBody = () => {
        const sorted = roster.slice().sort(getComparator(order, orderBy));
        return sorted.map((row, index) => (
            <RosterTableRow key={index} row={row} hasStudentGroups={hasStudentGroups} onRowClick={handleRowClick} />
        ));
    };

    const getStudentData = (studentId) => roster.find((x) => x.student_id === studentId);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: RosterTableColumns) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleCloseEdit = () => {
        setOpenEditModal(false);
    };

    const handleCloseDelete = () => {
        setOpenDeleteModal(false);
    };

    const handleFinishSendingCalendar = () => {
        setSendingCalendarInvite(false);
    };

    const handleInviteStudentsClick = async () => {
        if (!userUuid) {
            console.error('userUuid is not defined');
            return;
        }

        const searchParams = new URLSearchParams({ user_uuid: userUuid });
        if (classId) searchParams.append('class_id', classId);
        if (classUuid) searchParams.append('class_uuid', classUuid);
        if (isDemo) searchParams.append('isDemo', isDemo.toString());

        history.push(`/inviteStudents?${searchParams}`);
    };

    const handleDownloadCalendarInviteClick = async () => {
        const { api } = getUrls();
        const uri = `${api}${CLASS_CALENDAR}?class_id=${classUuid}&variant=exclude_attendees`;
        const {
            data: { ical }
        } = await axios.get(uri, { headers: getUserUuidHeaders(userUuid as string) });
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/calendar;charset=utf-8,${encodeURI(ical)}`);
        element.setAttribute('download', 'event.ics');
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleEmailInviteClick = async () => {
        setSendingCalendarInvite(true);
        const { api } = getUrls();
        const uri = `${api}${CLASS_CALENDAR_EMAIL}?class_id=${classUuid}&variant=exclude_attendees`;
        await axios.get(uri, { headers: getUserUuidHeaders(userUuid as string) });
        handleFinishSendingCalendar();
    };

    const rosterControls: NavControlItem[] = [];

    if (!loading) {
        if (adminSettings?.send_invite_emails) {
            rosterControls.push({
                label: t('email-calendar-invite'),
                dataTestId: 'send-calendar-invitations',
                onClick: handleEmailInviteClick
            });
        }
        if (isDevelopmentEnv()) {
            rosterControls.push({
                label: t('download-calendar-invite'),
                dataTestId: 'download-calendar-invitations',
                onClick: handleDownloadCalendarInviteClick
            });
        }
    }

    if (showAddStudentsButton) {
        rosterControls.push({
            label: t('add-students'),
            dataTestId: 'invite-users',
            onClick: handleInviteStudentsClick
        });
    }

    if (onBack) {
        rosterControls.push({
            label: '',
            ariaLabel: t('back'),
            dataTestId: 'button-back',
            icon: <ArrowBackIos />,
            onClick: onBack
        });
    }

    return (
        <ClassContainer.Provider initialState={{ classData }}>
            <MainLayout
                icon={mdiClipboardAccountOutline}
                title={t('roster-title')}
                controls={rosterControls}
                narrowStyle={isNarrowStyle}
                loading={fetchingStudents}
            >
                <Loadable fullScreen={isFullScreen} isLoading={loading}>
                    <TableContainer>
                        <Table>
                            <RosterTableHead
                                order={order}
                                orderBy={orderBy}
                                hasStudentGroups={hasStudentGroups}
                                onRequestSort={handleRequestSort}
                            />
                            <TableBody>{renderTableBody()}</TableBody>
                        </Table>

                        <RosterEditModal
                            open={openEditModal}
                            onClose={handleCloseEdit}
                            data={getStudentData(selectedStudentId)}
                        />
                        <RosterDeleteModal
                            open={openDeleteModal}
                            onClose={handleCloseDelete}
                            data={getStudentData(selectedStudentId)}
                        />
                        <RosterSendingCalendarInviteModal
                            open={sendingCalendarInvite}
                            onClose={handleFinishSendingCalendar}
                        />
                    </TableContainer>
                </Loadable>
            </MainLayout>
        </ClassContainer.Provider>
    );
};
export default RosterView;
